import { Title, Stack } from '@mantine/core';
import {
  AddEditMatterFormData,
  Form,
  HumanContact,
  OrganizationContact,
} from '../../types';
import { v4 as uuidv4 } from 'uuid';
import { ContactSearchSelection } from '../../../ContactSearchField/ContactSearchField';
import styles from './ContactsSection.module.css';
import { useAuthenticatedSession } from '@/authenticatedSession/AuthenticatedSessionContext';
import { FirmDatabase } from '@/database/firmDatabase';
import { TypesenseHuman, TypesenseOrganization } from '@/lib/typesense/types';
import HumanContactForm from './HumanContactForm';
import { ContactRole } from '@/database/baseTypes';
import { AuthenticatedSession } from '@/authenticatedSession/authenticatedSession';
import { OrganizationOutline } from './OrganizationOutline';
import AddContactPanel from './AddContactPanel';
import { useState } from 'react';

// Defines a union of the keys of object T that have the value of type V
type KeyOfType<T, V> = {
  [K in keyof T]: T[K] extends V ? K : never;
}[keyof T];

interface Props {
  title: string;
  collectionPath: KeyOfType<
    AddEditMatterFormData,
    (HumanContact | OrganizationContact)[]
  >;
  primaryContactIdPath?: KeyOfType<AddEditMatterFormData, string | null>;
  contactRole: ContactRole;

  getAlreadyAddedContactIds: () => string[];
  form: Form;
  shouldExpandAddPanelOnMount: boolean;
}

const ContactsSection = ({
  title,
  collectionPath,
  primaryContactIdPath,
  contactRole,
  getAlreadyAddedContactIds,
  form,
  shouldExpandAddPanelOnMount,
}: Props) => {
  const authSession = useAuthenticatedSession();

  const insertContact = (contact: HumanContact | OrganizationContact) => {
    if (
      contact.type === 'human' &&
      primaryContactIdPath &&
      form.getValues()[primaryContactIdPath] === null
    ) {
      form.setValues({
        [primaryContactIdPath]: contact.tempId,
      });
    }
    form.insertListItem(collectionPath, contact);
  };

  const handleContactSearchSelection = async (
    selection: ContactSearchSelection,
  ) => {
    const contact = await convertSearchSelectionToContact({
      authSession,
      selection,
      contactRole,
    });
    if (!contact) {
      return;
    }
    insertContact(contact);
  };

  const handleRepresentativeSearchSelection = async (
    selection: ContactSearchSelection,
    organizationTempId: string,
  ) => {
    console.log(
      'handleRepresentativeSearchSelection',
      selection,
      organizationTempId,
    );
    const contact = (await convertSearchSelectionToContact({
      authSession,
      selection,
      contactRole,
    })) as HumanContact;
    if (!contact) {
      return;
    }

    if (
      primaryContactIdPath &&
      form.getValues()[primaryContactIdPath] === null
    ) {
      form.setValues({
        [primaryContactIdPath]: contact.tempId,
      });
    }

    const collection = form.getValues()[collectionPath];
    const organization = collection.find(
      (c) => c.tempId === organizationTempId,
    ) as OrganizationContact;
    if (!organization) {
      return;
    }
    organization.representatives.push(contact);
    form.setValues({
      [collectionPath]: collection,
    });
  };

  const contacts = form.getValues()[collectionPath];

  const [isAddContactFormCollapsed, setIsAddContactFormCollapsed] = useState(
    !shouldExpandAddPanelOnMount,
  );

  return (
    <section>
      <Stack gap='xs'>
        <Title order={2} size='h3'>
          {title}
        </Title>

        <div className={styles.contactsContainer}>
          {contacts.length > 0 && (
            <ul>
              {contacts.map((contact, index) => (
                <li key={contact.tempId}>
                  {contact.type === 'human' ? (
                    <HumanContactForm
                      key={contact.tempId}
                      title={`Person`}
                      canBePrimary={primaryContactIdPath !== undefined}
                      human={contact}
                      isPrimary={
                        primaryContactIdPath
                          ? form.getValues()[primaryContactIdPath] ===
                            contact.tempId
                          : false
                      }
                      onChange={(human) => {
                        form.removeListItem(collectionPath, index);
                        form.insertListItem(collectionPath, human, index);
                      }}
                      onWantsPrimary={() => {
                        if (primaryContactIdPath) {
                          form.setValues({
                            [primaryContactIdPath]: contact.tempId,
                          });
                        }
                      }}
                      onDelete={() => {
                        if (
                          primaryContactIdPath &&
                          form.getValues()[primaryContactIdPath] ===
                            contact.tempId
                        ) {
                          form.setValues({
                            [primaryContactIdPath]: null,
                          });
                        }
                        form.removeListItem(collectionPath, index);
                      }}
                    />
                  ) : (
                    <OrganizationOutline
                      organization={contact}
                      primaryContactTempId={
                        primaryContactIdPath
                          ? form.getValues()[primaryContactIdPath]
                          : null
                      }
                      autoFocus='none'
                      onContactSearchSelection={(selection) => {
                        handleRepresentativeSearchSelection(
                          selection,
                          contact.tempId,
                        );
                      }}
                      getAlreadyAddedContactIds={getAlreadyAddedContactIds}
                      humanCanBePrimary={primaryContactIdPath !== undefined}
                      onHumanWantsPrimary={(tempId) => {
                        if (primaryContactIdPath) {
                          form.setValues({
                            [primaryContactIdPath]: tempId,
                          });
                        }
                      }}
                      onHumanChange={(human) => {
                        const collection = form.getValues()[collectionPath];
                        const organization = collection.find(
                          (c) => c.tempId === contact.tempId,
                        ) as OrganizationContact;
                        organization.representatives =
                          organization.representatives.map((rep) =>
                            rep.tempId === human.tempId ? human : rep,
                          );
                        form.removeListItem(collectionPath, index);
                        form.insertListItem(
                          collectionPath,
                          organization,
                          index,
                        );
                      }}
                      onHumanDelete={(index) => {
                        const collection = form.getValues()[collectionPath];
                        const organization = collection.find(
                          (c) => c.tempId === contact.tempId,
                        ) as OrganizationContact;
                        const representative =
                          organization.representatives[index];
                        if (
                          primaryContactIdPath &&
                          form.getValues()[primaryContactIdPath] ===
                            representative.tempId
                        ) {
                          form.setValues({
                            [primaryContactIdPath]: null,
                          });
                        }
                        organization.representatives =
                          organization.representatives.filter(
                            (_, i) => i !== index,
                          );
                        form.setValues({
                          [collectionPath]: collection,
                        });
                      }}
                      onOrganizationDelete={() => {
                        form.removeListItem(collectionPath, index);
                      }}
                      onOrganizationChange={(organization) => {
                        form.removeListItem(collectionPath, index);
                        form.insertListItem(
                          collectionPath,
                          organization,
                          index,
                        );
                      }}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
          <Stack gap='0'>
            <AddContactPanel
              panelTitle={`Add ${
                contactRole === 'client' ? 'a client' : 'an adverse party'
              }`}
              searchFieldLabel={`Search for an existing contact`}
              collapsedAccessibilityLabel={`Expand options to add ${
                contactRole === 'client' ? 'a client' : 'an adverse party'
              }`}
              expandedAccessibilityLabel={`Hide options to add ${
                contactRole === 'client' ? 'a client' : 'an adverse party'
              }`}
              onContactSelection={(selection) => {
                handleContactSearchSelection(selection);
                setIsAddContactFormCollapsed(true);
              }}
              getAlreadyAddedContactIds={getAlreadyAddedContactIds}
              searchFieldPlaceholder={`Search for an existing contact`}
              searchMode='all'
              isCollapsed={isAddContactFormCollapsed}
              onToggleCollapse={() => {
                setIsAddContactFormCollapsed(!isAddContactFormCollapsed);
              }}
            />
          </Stack>
        </div>
      </Stack>
    </section>
  );
};

export default ContactsSection;

const convertSearchSelectionToContact = async (props: {
  authSession: AuthenticatedSession | undefined;
  selection: ContactSearchSelection;
  contactRole: ContactRole;
}) => {
  const { authSession, selection, contactRole } = props;

  if (!authSession) {
    return undefined;
  }

  if (selection.type === 'existingHuman') {
    return await convertHumanSearchResultToHumanContact(
      authSession.db,
      selection.value,
      contactRole,
    );
  } else if (selection.type === 'existingOrganization') {
    return convertOrganizationSearchResultToOrganizationContact(
      selection.value,
      contactRole,
    );
  } else if (selection.type === 'newHuman') {
    const contactTempId = uuidv4();

    const human: HumanContact = {
      tempId: contactTempId,
      firestoreId: null,
      firstName: '',
      lastName: '',
      phoneNumbers: [],
      primaryPhoneNumberId: null,
      emailAddresses: [],
      primaryEmailAddressId: null,
      preferredLanguage: 'en',
      role: contactRole,
      type: 'human',
    };
    return human;
  } else if (selection.type === 'newOrganization') {
    const organization: OrganizationContact = {
      tempId: uuidv4(),
      firestoreId: null,
      companyName: selection.query,
      role: contactRole,
      type: 'organization',
      phoneNumbers: [],
      primaryPhoneNumberId: null,
      emailAddresses: [],
      primaryEmailAddressId: null,
      representatives: [],
    };
    return organization;
  }
};

const convertHumanSearchResultToHumanContact = async (
  firmDb: FirmDatabase,
  human: TypesenseHuman,
  role: ContactRole,
): Promise<HumanContact> => {
  const humanSnapshot = await firmDb.getHuman(human.id);
  const humanData = humanSnapshot.data();
  if (!humanData) {
    console.error(`Human with ID ${human.id} not found in firestore`);
    throw new Error(`Human with ID ${human.id} not found in firestore`);
  }

  const phoneNumbers = humanData.phoneNumbers ?? [];
  const primaryPhoneNumberId = humanData.primaryPhoneNumberId ?? uuidv4();
  if (phoneNumbers.length === 0) {
    phoneNumbers.push({ id: primaryPhoneNumberId, value: '' });
  }

  const emailAddresses = humanData.emailAddresses ?? [];
  const primaryEmailAddressId = humanData.primaryEmailAddressId ?? uuidv4();
  if (emailAddresses.length === 0) {
    emailAddresses.push({ id: primaryEmailAddressId, value: '' });
  }

  return {
    tempId: uuidv4(),
    firestoreId: humanSnapshot.id,
    firstName: humanData.firstName,
    lastName: humanData.lastName,
    phoneNumbers,
    primaryPhoneNumberId,
    emailAddresses,
    primaryEmailAddressId,
    preferredLanguage: humanData.preferredLanguage || 'en',
    role,
    type: 'human',
  };
};

const convertOrganizationSearchResultToOrganizationContact = (
  organization: TypesenseOrganization,
  role: ContactRole,
): OrganizationContact => {
  return {
    tempId: organization.id,
    firestoreId: organization.id,
    companyName: organization.companyName,
    role,
    type: 'organization',
    phoneNumbers: [],
    primaryPhoneNumberId: null,
    emailAddresses: [],
    primaryEmailAddressId: null,
    representatives: [],
  };
};
